import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

// Create the context
const AudioContext = createContext();

// Custom hook to use the audio context
export const useAudio = () => useContext(AudioContext);

// Provider component
export const AudioProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const audioRef = useRef(null);

  // Play a track
  const playTrack = async (track) => {
    // If the same track is clicked, toggle play/pause
    if (currentTrack && currentTrack.id === track.id) {
      togglePlay();
      return;
    }

    try {
      // Set the current track
      setCurrentTrack(track);

      // Reset player state
      setCurrentTime(0);
      setIsPlaying(false);

      // Get the audio URL
      let audioUrl = '';
      
      // If the track already has a URL, use it
      if (track.audioUrl) {
        audioUrl = track.audioUrl;
      } 
      // Otherwise, get a pre-signed URL for the audio file
      else if (track.fileKey) {
        const response = await axios.get(`${config.API_URL}/get-presigned-url?key=${encodeURIComponent(track.fileKey)}`);
        if (response.data && response.data.presignedUrl) {
          audioUrl = response.data.presignedUrl;
        } else {
          throw new Error('Failed to get pre-signed URL');
        }
      } else {
        throw new Error('No audio URL or file key provided');
      }

      // Set the audio source
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.volume = volume;
        
        // Play after a small delay to ensure the browser has loaded the audio
        setTimeout(() => {
          audioRef.current.play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.error('Error playing audio:', error);
            });
        }, 100);
      }
    } catch (error) {
      console.error('Error setting up audio playback:', error);
    }
  };

  // Toggle play/pause
  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
    setIsPlaying(!isPlaying);
  };

  // Skip to next track (placeholder for future implementation)
  const nextTrack = () => {
    // This would be implemented when we have playlists or albums
    console.log('Next track functionality not implemented yet');
  };

  // Skip to previous track (placeholder for future implementation)
  const previousTrack = () => {
    // This would be implemented when we have playlists or albums
    console.log('Previous track functionality not implemented yet');
  };

  // Seek to a specific time
  const seekTo = (time) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  // Set the volume
  const setAudioVolume = (newVolume) => {
    if (!audioRef.current) return;
    audioRef.current.volume = newVolume;
    setVolume(newVolume);
  };

  // Format time for display (convert seconds to mm:ss)
  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleDurationChange = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Optionally auto-play next track here
    };

    // Add event listeners
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    // Clean up event listeners
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // The value that will be provided to consumers of this context
  const value = {
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    volume,
    formatTime,
    playTrack,
    togglePlay,
    nextTrack,
    previousTrack,
    seekTo,
    setVolume: setAudioVolume
  };

  return (
    <AudioContext.Provider value={value}>
      <audio ref={audioRef} crossOrigin="anonymous" />
      {children}
    </AudioContext.Provider>
  );
};
